import * as R from 'ramda';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { apiEntitlementHotlist } from '../../core/redux/api/endpoints';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../core/redux/IAppState';
import { convertUtcStringDateToDate } from '../../language/time';
import { Resort } from '../../resortDetails/resort';
import { EntitlementHotlist, QuickEntitlement } from './models';
import { setEntitlementHotlistTitle } from './module';
import { assoc } from '../../language/module';


const KEY_ACTION = ACTION_TYPE.GET_ENTITLEMENT_HOTLIST_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.GET_ENTITLEMENT_HOTLIST_REQUEST,
  ACTION_TYPE.GET_ENTITLEMENT_HOTLIST_RESPONSE,
  ACTION_TYPE.GET_ENTITLEMENT_HOTLIST_ERROR,
];


/**
 * Makes an action that can be dispatched to load an entitlement's list of hotlists, active and inactive.
 * @param entitlement The entitlement whose hotlists to retrieve
 * @param resortList List of resorts to map hotlist data to full names.  Should include "ALL" for global hotlists.
 */
export const getEntitlementHotlistAction = (entitlement: QuickEntitlement, resortList: Resort[]) => makeApiAction(
  {
    method:   'GET',
    endpoint: apiEntitlementHotlist(entitlement.entitlementId),
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { entitlement, resortList } })) as RsaaTypes,
    bailout:  state => !!getLoading(state, `${entitlement.entitlementId}`, KEY_ACTION)
                       || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<EntitlementHotlist[]> = (state, action, error, isLoading, payload) => {
  const resortList    = action.meta.resortList as Resort[];
  const entitlement   = action.meta.entitlement as QuickEntitlement;
  const entitlementId = `${entitlement.entitlementId}`;

  if (isLoading) {
    return addLoading(state, entitlementId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, entitlementId, KEY_ACTION, error, { entitlement });
  }

  const adjustedState = removeLoadingAndError(withBreaker, entitlementId, KEY_ACTION);

  if (!payload) {
    return adjustedState;
  }

  // Add titles to the resort hotlists for easier display later
  const augmentedPayload = R.map(hotEntry => setEntitlementHotlistTitle(hotEntry, entitlement, resortList), payload!);

  // Convert provided dates from UTC to local times so they match other API endpoints which give us local time
  const dateFixedPayload = R.map(
    (hotEntry) => R.mergeDeepRight(hotEntry, {
      createdDate:  convertUtcStringDateToDate(hotEntry, 'createdDate'),
      modifiedDate: convertUtcStringDateToDate(hotEntry, 'modifiedDate'),
    }),
    augmentedPayload,
  );

  return assoc(
    'entitlementHotlists',
    adjustedState.entitlementHotlists.add(entitlementId, dateFixedPayload),
    adjustedState,
  );

};

export const getEntitlementHotlistReducer = makeApiReducer<EntitlementHotlist[]>(ACTIONS, KEY_ACTION, reducer);
